<script setup lang="ts">
/**
 * TheHeader — hoofdnavigatie (Zeroheight-stijl).
 *
 * De menu-items zijn de hoofdcategorieën van de documentatie; klikken
 * navigeert naar de eerste pagina van de categorie en beperkt het
 * linkermenu tot die categorie. Zichtbaar voor iedereen (bezoekers
 * krijgen alleen zichtbare categorieën van de API). Beheer-chrome
 * (voorbeeldknop, instellingen, gebruikersmenu) leeft in TheAdminBar.
 */

import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../../stores/auth'
import { useUiStore } from '../../stores/ui'
import { useThemeStore } from '../../stores/theme'
import { useSectionStore } from '../../stores/sections'
import { useAccessStore } from '../../stores/access'
import { useNotificationStore } from '../../stores/notifications'
import { useAnnounce } from '../../composables/useAnnounce'
import { useDragReorder } from '../../composables/useDragReorder'
import PentricalLogo from '../common/PentricalLogo.vue'
import DocSearch from '../search/DocSearch.vue'
import type { Section, Page } from '../../types'
import {
  Menu as MenuIcon,
  X,
  Layers,
  Plus,
  Eye,
  EyeOff,
  MoreHorizontal,
  Pencil,
  ArrowLeft,
  ArrowRight,
  FolderInput,
  ChevronRight,
  Lock,
  Trash2,
} from 'lucide-vue-next'
import CategoryAccessDialog from '../access/CategoryAccessDialog.vue'
import AccessGate from '../access/AccessGate.vue'

const authStore = useAuthStore()
const ui = useUiStore()
const theme = useThemeStore()
const router = useRouter()
const store = useSectionStore()
const access = useAccessStore()
const notifications = useNotificationStore()
const { announcePolite } = useAnnounce()
const { t } = useI18n()

/* ── Categorie openen ───────────────────────────────────────────
   Eerste pagina: eigen pagina's eerst, anders (recursief) de eerste
   pagina van de eerste subcategorie. */
function firstPageOf(section: Section): { page: Page; section: Section } | null {
  if (section.pages?.length) return { page: section.pages[0], section }
  for (const child of section.children ?? []) {
    const hit = firstPageOf(child)
    if (hit) return hit
  }
  return null
}

/* Spiegel van SidebarNav.pageLink: beheer id-links, bezoekers slug-links. */
function firstPageLink(category: Section): string | null {
  const hit = firstPageOf(category)
  if (!hit) return null
  return ui.canEdit
    ? `/pages/${hit.page.id}`
    : `/docs/${hit.section.slug}/${hit.page.slug}`
}

function openCategory(category: Section) {
  // Afgeschermde categorie: een bezoeker heeft hier niets om naartoe te
  // navigeren (de API stuurt de inhoud niet mee). Zonder dit zou klikken op
  // het slotje niets doen — nu opent het ontgrendelformulier.
  if (category.isLocked) {
    lockedCategory.value = category
    return
  }

  store.setActiveRoot(category.id)
  const link = firstPageLink(category)
  if (link) {
    router.push(link)
    return
  }

  // Lege categorie: voor een redacteur naar het pagina-overzicht met deze
  // categorie als filter - daar staat de "Nieuwe pagina"-knop klaar. Zonder
  // navigatie leek de klik niets te doen (alleen de zijbalk wisselde mee),
  // en dat was in gebruikerstests onvindbaar. Bezoekers houden de
  // menu-wissel: voor hen valt er niets aan te maken.
  if (ui.canEdit) {
    router.push({ path: '/pages', query: { section: String(category.id) } })
  }
}

/* ── Sitebreed slot: ontgrendelen vanuit de header ──────────────
   Staat de site op slot terwijl er tóch iets openbaars is, dan ziet de
   bezoeker de gewone site (met alleen dat openbare deel) en is dit de weg
   naar de rest. Zonder deze knop zou hij het wachtwoord nergens kwijt kunnen:
   het slotscherm over de volle breedte blijft dan immers achterwege. */
const showSiteUnlock = computed(() => access.siteLocked && !ui.canEdit)
const siteUnlockOpen = ref(false)

async function onSiteUnlocked() {
  siteUnlockOpen.value = false
  await access.refresh()
  await store.fetchTree(ui.visitorView)
}

/* ── Bezoeker klikt op een categorie met een slotje ───────────── */
const lockedCategory = ref<Section | null>(null)

async function onCategoryUnlocked() {
  const category = lockedCategory.value
  lockedCategory.value = null
  await store.fetchTree(ui.visitorView)
  const fresh = category ? store.tree.find(c => c.id === category.id) : null
  if (fresh) openCategory(fresh)
}

/* ── Inline invoer (nieuwe categorie / hernoemen) ───────────────
   Zelfde patroon als PageTabs: autofocus, Enter/blur commit, Escape
   annuleert. Hernoem-input staat in een v-for → aparte array-ref. */
const addingCategory = ref(false)
const renamingId = ref<number | null>(null)
const inputValue = ref('')
const addInputEl = ref<HTMLInputElement | null>(null)
const renameInputEl = ref<HTMLInputElement[]>([])

async function startAddCategory() {
  closeMenu()
  renamingId.value = null
  addingCategory.value = true
  inputValue.value = ''
  await nextTick()
  addInputEl.value?.focus()
}

async function startRename(category: Section) {
  closeMenu()
  addingCategory.value = false
  renamingId.value = category.id
  inputValue.value = category.name
  await nextTick()
  const el = renameInputEl.value[0]
  if (el) { el.focus(); el.select() }
}

function cancelInput() {
  addingCategory.value = false
  renamingId.value = null
}

async function commitInput() {
  const value = inputValue.value.trim()
  const wasAdding = addingCategory.value
  const renameId = renamingId.value
  cancelInput()
  if (!value) return

  try {
    if (wasAdding) {
      // Achteraan in het menu, na de bestaande hoofdcategorieën.
      await store.createSection({ name: value, parentId: null, sortOrder: store.tree.length })
    } else if (renameId !== null) {
      const current = store.tree.find(c => c.id === renameId)
      if (!current || value === current.name) return
      await store.updateSection(renameId, { name: value })
    }
  } catch (err: any) {
    notifications.error(err.response?.data?.message || t('nav.saveFailed'))
  }
}

/* ── Flyout-menu per categorie (zelfde patroon als PageTabs) ──── */
const menuCategory = ref<Section | null>(null)
const menuPos = ref({ top: 0, left: 0, maxHeight: 9999 })
const menuEl = ref<HTMLElement | null>(null)
let menuAnchor: HTMLElement | null = null

const menuIndex = computed(() =>
  menuCategory.value ? store.tree.findIndex(c => c.id === menuCategory.value!.id) : -1
)

function openMenu(event: MouseEvent, category: Section) {
  const anchor = event.currentTarget as HTMLElement
  menuAnchor = anchor
  menuCategory.value = category
  showMoveTargets.value = false

  const rect = anchor.getBoundingClientRect()
  menuPos.value = { top: rect.bottom + 4, left: rect.left, maxHeight: window.innerHeight - 16 }

  nextTick(() => {
    positionMenu()
    menuEl.value?.querySelector('button')?.focus()
  })
}

/** Klem het (position: fixed) menu binnen de viewport; zie SidebarNav. */
function positionMenu() {
  const el = menuEl.value
  const anchor = menuAnchor
  if (!el || !anchor) return

  const margin = 8
  const rect = anchor.getBoundingClientRect()
  const menuW = el.offsetWidth
  const available = window.innerHeight - margin * 2
  const menuH = Math.min(el.offsetHeight, available)

  let top = rect.bottom + 4
  if (top + menuH > window.innerHeight - margin) {
    const aboveTop = rect.top - 4 - menuH
    top = aboveTop >= margin
      ? aboveTop
      : Math.max(margin, window.innerHeight - margin - menuH)
  }

  let left = Math.min(rect.left, window.innerWidth - margin - menuW)
  left = Math.max(margin, left)

  menuPos.value = { top, left, maxHeight: available }
}

function closeMenu(returnFocus = false) {
  menuCategory.value = null
  if (returnFocus && menuAnchor) menuAnchor.focus()
  menuAnchor = null
}

function onDocumentClick(event: MouseEvent) {
  if (menuEl.value && !menuEl.value.contains(event.target as Node)) closeMenu()
}
function onDocumentKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape' && menuCategory.value) closeMenu(true)
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true)
  document.addEventListener('keydown', onDocumentKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true)
  document.removeEventListener('keydown', onDocumentKeydown)
})

/* ── Menu-acties ────────────────────────────────────────────── */
async function runAction(action: () => Promise<void>, errorMessage: string) {
  try {
    await action()
  } catch (err: any) {
    notifications.error(err.response?.data?.message || errorMessage)
  }
}

async function toggleVisibility() {
  const category = menuCategory.value
  if (!category) return
  closeMenu(true)
  await runAction(async () => {
    await store.updateSection(category.id, { isVisible: !category.isVisible })
  }, t('nav.visibilityFailed'))
}

async function persistOrder(reordered: Section[]) {
  await store.reorderSections(reordered.map((c, i) => ({ id: c.id, sortOrder: i, parentId: null })))
}

async function moveBy(offset: number) {
  const category = menuCategory.value
  const index = menuIndex.value
  if (!category || index < 0) return
  const newIndex = index + offset
  if (newIndex < 0 || newIndex >= store.tree.length) return
  closeMenu(true)

  const reordered = [...store.tree]
  const [moved] = reordered.splice(index, 1)
  reordered.splice(newIndex, 0, moved)

  await runAction(async () => {
    await persistOrder(reordered)
    announcePolite(t('nav.categoryMovedTo', { name: moved.name, position: newIndex + 1, total: store.tree.length }))
  }, t('nav.moveFailed'))
}

/* ── Verplaatsen naar een andere categorie (degraderen) ─────────
   Spiegel van SidebarNav's "Verplaats naar": een hoofdmenu-item wordt
   subcategorie van een andere hoofdcategorie. Zelfde regel als daar
   (en als de backend afdwingt): een item mét subcategorieën kan zelf
   geen subcategorie worden — max. diepte is 2. */
const showMoveTargets = ref(false)

function toggleMoveTargets() {
  showMoveTargets.value = !showMoveTargets.value
  nextTick(positionMenu)
}

const menuHasChildren = computed(() =>
  (menuCategory.value?.children?.length ?? 0) > 0
)

const moveTargets = computed(() => {
  const category = menuCategory.value
  if (!category || menuHasChildren.value) return []
  return store.tree.filter(c => c.id !== category.id)
})

async function moveToCategory(targetId: number) {
  const category = menuCategory.value
  if (!category) return
  closeMenu(true)

  await runAction(async () => {
    await store.updateSection(category.id, { parentId: targetId })
    // Volg het verplaatste item: open de bestemmingscategorie zodat
    // het direct zichtbaar is in het linkermenu.
    store.setActiveRoot(targetId)
    const destination = store.tree.find(c => c.id === targetId)
    announcePolite(t('nav.movedToCategory', { name: category.name, destination: destination?.name ?? '' }))
  }, t('nav.moveFailed'))
}

async function confirmDelete() {
  const category = menuCategory.value
  if (!category) return
  closeMenu(true)

  if (!confirm(t('nav.confirmDeleteCategory', { name: category.name }))) return

  await runAction(async () => {
    await store.deleteSection(category.id)
    announcePolite(t('nav.categoryDeleted', { name: category.name }))
  }, t('nav.deleteFailed'))
}

/* ── Slot van een categorie (Instellingen → Access voor de hele site) ── */
const accessDialogCategory = ref<Section | null>(null)

function openAccessDialog() {
  const category = menuCategory.value
  if (!category) return
  closeMenu(true)
  accessDialogCategory.value = category
}

/** Zet deze categorie (of een voorouder) een eigen slot? */
function hasOwnLock(category: Section): boolean {
  return Boolean(category.accessMode && category.accessMode !== 'inherit')
}

/* ── Drag & drop (horizontaal, additief naast het menu) ───────── */
const dnd = useDragReorder('x')

async function onDrop() {
  const reordered = dnd.apply(store.tree)
  dnd.reset()
  if (!reordered) return
  await runAction(() => persistOrder(reordered), t('nav.moveFailed'))
}
</script>

<template>
  <header
    class="bg-white border-b border-gray-200 z-40"
    role="banner"
  >
    <div class="px-4 sm:px-6">
      <div class="flex items-center gap-4 h-16">
        <!-- Logo (volle breedte header: altijd zichtbaar).
             Instelbaar via Instellingen → Thema: afbeelding of tekst. -->
        <RouterLink
          to="/"
          class="flex items-center gap-2 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded flex-shrink-0"
        >
          <img
            v-if="theme.logoType === 'image' && theme.logoUrl"
            :src="theme.logoUrl"
            :alt="theme.logoText"
            class="max-h-10 w-auto"
          />
          <!-- Nieuwe oplevering (nog geen eigen thema): Pentrical-logo -->
          <PentricalLogo v-else-if="theme.isDefault" img-class="max-h-8 w-auto" />
          <template v-else>
            <Layers class="w-6 h-6 text-primary-600" aria-hidden="true" />
            <span>{{ theme.logoText }}</span>
          </template>
        </RouterLink>

        <!-- Duwt zoeken en de menuknop naar rechts op smalle schermen. Vanaf
             lg doet de hoofdnavigatie dat zelf met flex-1. -->
        <div class="flex-1 lg:hidden" aria-hidden="true"></div>

        <!-- Hoofdnavigatie: hoofdcategorieën als menu-items.
             In beheer: hover-⋯-menu per item (zoals tabs) + drag & drop. -->
        <!-- De flex-gap (1rem) geldt voor álle header-items; om alléén logo↔menu
             ruimer te maken zet ik de rest als margin op de nav:
             gap 1rem + lg:ml-8 (2rem) = 3rem tussen logo en hoofdmenu. -->
        <nav class="hidden lg:flex items-stretch gap-1 flex-1 self-stretch lg:ml-8" :aria-label="t('a11y.mainNav')">
          <div
            v-for="(category, index) in store.tree"
            :key="category.id"
            class="category-item group relative flex items-center"
            :class="{ 'opacity-40': dnd.isDragged('root', index) }"
            :draggable="ui.canEdit ? 'true' : undefined"
            @dragstart="ui.canEdit && dnd.start($event, 'root', index)"
            @dragover="ui.canEdit && dnd.over($event, 'root', index)"
            @drop.prevent="ui.canEdit && onDrop()"
            @dragend="ui.canEdit && dnd.reset()"
          >
            <!-- Invoeg-indicator tijdens slepen -->
            <span
              v-if="dnd.lineBefore('root', index)"
              class="drop-indicator left-0"
              aria-hidden="true"
            ></span>
            <span
              v-if="dnd.lineAfterLast('root', index, store.tree.length)"
              class="drop-indicator right-0"
              aria-hidden="true"
            ></span>

            <input
              v-if="renamingId === category.id"
              ref="renameInputEl"
              v-model="inputValue"
              class="mx-1 w-40 rounded-md border border-primary-500 px-2 py-1 text-sm"
              :aria-label="t('nav.renameCategoryLabel')"
              @keydown.enter.prevent="commitInput"
              @keydown.esc="cancelInput"
              @blur="commitInput"
              @click.stop
            >
            <button
              v-else
              type="button"
              class="h-full flex items-center gap-1.5 px-3 text-base font-medium border-b-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              :class="[
                category.id === store.activeRoot?.id
                  ? 'border-primary-600 text-primary-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300',
                { 'opacity-60': !category.isVisible && ui.canEdit },
              ]"
              :aria-current="category.id === store.activeRoot?.id ? 'true' : undefined"
              @click="openCategory(category)"
            >
              {{ category.name }}
              <EyeOff
                v-if="!category.isVisible && ui.canEdit"
                class="w-3.5 h-3.5 text-amber-600 flex-shrink-0"
                aria-hidden="true"
              />
              <!-- Slotje: in beheer waar een eigen slot is gezet, voor
                   bezoekers op de categorieën waar ze niet in mogen. -->
              <Lock
                v-if="ui.canEdit ? hasOwnLock(category) : category.isLocked"
                class="w-3.5 h-3.5 text-gray-400 flex-shrink-0"
                :aria-label="t('access.category.lockedLabel')"
              />
            </button>

            <button
              v-if="ui.canEdit && renamingId !== category.id"
              type="button"
              class="category-menu-btn"
              :class="{ 'menu-open': menuCategory?.id === category.id }"
              :aria-label="t('nav.actionsFor', { name: category.name })"
              :aria-expanded="menuCategory?.id === category.id"
              aria-haspopup="menu"
              @click.stop="openMenu($event, category)"
            >
              <MoreHorizontal class="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </div>

          <!-- Nieuwe hoofdcategorie (alleen beheer), inline zoals tabs -->
          <template v-if="ui.canEdit">
            <div class="flex items-center">
              <input
                v-if="addingCategory"
                ref="addInputEl"
                v-model="inputValue"
                class="w-44 rounded-md border border-primary-500 px-2 py-1 text-sm"
                :placeholder="t('nav.newCategoryPlaceholder')"
                :aria-label="t('nav.newCategoryNameLabel')"
                @keydown.enter.prevent="commitInput"
                @keydown.esc="cancelInput"
                @blur="commitInput"
              >
              <!-- Met zichtbaar label: een kaal plusje bleek in
                   gebruikerstests niet te vinden. -->
              <button
                v-else
                type="button"
                class="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md border border-dashed border-gray-300 text-sm font-medium text-gray-600 hover:text-primary-700 hover:border-primary-400 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 whitespace-nowrap"
                @click="startAddCategory"
              >
                <Plus class="w-4 h-4" aria-hidden="true" />
                {{ t('nav.newCategory') }}
              </button>
            </div>
          </template>
        </nav>

        <!-- Zoeken. Hier géén ml-auto: DocSearch heeft twee root-elementen
             (de knop én het zoekvenster), en dan erft een class op de
             component niet — zelfde valkuil als bij PageTabs. Het duwen doet
             de spacer hierboven, die wél een echt element is. -->
        <DocSearch />

        <!-- Inloggen (bezoekers; in voorbeeldweergave heeft de amber balk
             "Terug naar beheer", dus dan geen login-link) -->
        <!-- Ontgrendelen (sitebreed slot, maar er is openbaar deel zichtbaar) -->
        <button
          v-if="showSiteUnlock"
          type="button"
          class="flex-shrink-0 inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          @click="siteUnlockOpen = true"
        >
          <Lock class="w-4 h-4" aria-hidden="true" />
          {{ t('access.gate.unlockSite') }}
        </button>

        <RouterLink
          v-if="!authStore.isAuthenticated"
          to="/login"
          class="btn-primary flex-shrink-0"
        >
          {{ t('common.login') }}
        </RouterLink>

        <!-- Menuknop (< lg). Staat bewust als laatste item, dus uiterst
             rechts in de balk: dat is waar een duim op een telefoon komt en
             waar het uitschuifmenu vandaan komt. Links naast het logo, zoals
             het eerst stond, wees de knop de andere kant op dan de lade die
             hij opent. -->
        <button
          type="button"
          class="lg:hidden flex-shrink-0 p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          @click="ui.toggleMobileNav()"
          :aria-expanded="ui.isMobileNavOpen"
          aria-controls="mobile-menu"
        >
          <span class="sr-only">{{ ui.isMobileNavOpen ? t('nav.closeMenu') : t('nav.openMenu') }}</span>
          <component
            :is="ui.isMobileNavOpen ? X : MenuIcon"
            class="w-6 h-6"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  </header>

  <!-- Flyout-menu voor categorie-acties (zelfde patroon als PageTabs) -->
  <Teleport to="body">
    <transition name="menu-pop">
      <div
        v-if="menuCategory"
        ref="menuEl"
        role="menu"
        :aria-label="t('nav.actionsFor', { name: menuCategory.name })"
        class="fixed z-50 min-w-[220px] overflow-y-auto rounded-lg bg-white p-1.5 shadow-lg ring-1 ring-black ring-opacity-5 text-sm"
        :style="{ top: `${menuPos.top}px`, left: `${menuPos.left}px`, maxHeight: `${menuPos.maxHeight}px` }"
      >
        <button type="button" role="menuitem" class="menu-item" @click="startRename(menuCategory)">
          <Pencil class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.rename') }}
        </button>

        <div class="my-1 h-px bg-gray-100" role="separator"></div>

        <button type="button" role="menuitem" class="menu-item" @click="toggleVisibility">
          <component :is="menuCategory.isVisible ? EyeOff : Eye" class="w-4 h-4 text-gray-400" aria-hidden="true" />
          {{ menuCategory.isVisible ? t('nav.hideFromVisitors') : t('nav.makeVisible') }}
        </button>

        <button type="button" role="menuitem" class="menu-item" @click="openAccessDialog">
          <Lock class="w-4 h-4 text-gray-400" aria-hidden="true" />
          {{ t('nav.access') }}
          <span v-if="hasOwnLock(menuCategory)" class="ml-auto text-xs text-gray-400">
            {{ t('access.category.onBadge') }}
          </span>
        </button>

        <div class="my-1 h-px bg-gray-100" role="separator"></div>

        <button type="button" role="menuitem" class="menu-item" :disabled="menuIndex <= 0" @click="moveBy(-1)">
          <ArrowLeft class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.moveLeft') }}
        </button>
        <button type="button" role="menuitem" class="menu-item" :disabled="menuIndex >= store.tree.length - 1" @click="moveBy(1)">
          <ArrowRight class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.moveRight') }}
        </button>
        <button type="button" role="menuitem" class="menu-item" :aria-expanded="showMoveTargets" @click="toggleMoveTargets">
          <FolderInput class="w-4 h-4 text-gray-400" aria-hidden="true" /> {{ t('nav.moveToCategory') }}
          <ChevronRight class="ml-auto w-3.5 h-3.5 text-gray-400 transition-transform" :class="{ 'rotate-90': showMoveTargets }" aria-hidden="true" />
        </button>
        <div v-if="showMoveTargets" class="pl-7 py-0.5">
          <button
            v-for="destination in moveTargets"
            :key="destination.id"
            type="button"
            role="menuitem"
            class="menu-item text-[13px]"
            @click="moveToCategory(destination.id)"
          >
            {{ destination.name }}
          </button>
          <p v-if="menuHasChildren" class="px-2 py-1 text-xs text-gray-400">
            {{ t('nav.cannotMoveHasSubcategories') }}
          </p>
          <p v-else-if="moveTargets.length === 0" class="px-2 py-1 text-xs text-gray-400">
            {{ t('nav.noOtherCategories') }}
          </p>
        </div>

        <div class="my-1 h-px bg-gray-100" role="separator"></div>

        <button type="button" role="menuitem" class="menu-item text-red-700 hover:bg-red-50" @click.stop="confirmDelete">
          <Trash2 class="w-4 h-4 text-red-600" aria-hidden="true" /> {{ t('nav.deleteWithContent') }}
        </button>
      </div>
    </transition>
  </Teleport>

  <CategoryAccessDialog
    :section="accessDialogCategory"
    @close="accessDialogCategory = null"
  />

  <!-- Sitebreed slot ontgrendelen vanuit de header -->
  <Teleport to="body">
    <AccessGate
      v-if="siteUnlockOpen"
      variant="dialog"
      :mode="access.mode === 'team' ? 'team' : 'password'"
      :hint="access.hint"
      @unlocked="onSiteUnlocked"
      @close="siteUnlockOpen = false"
    />
  </Teleport>

  <!-- Bezoeker klikte op een afgeschermde categorie -->
  <Teleport to="body">
    <AccessGate
      v-if="lockedCategory"
      variant="dialog"
      :section-id="lockedCategory.id"
      :section-name="lockedCategory.name"
      :mode="lockedCategory.lockMode === 'team' ? 'team' : 'password'"
      :hint="lockedCategory.lockHint"
      @unlocked="onCategoryUnlocked"
      @close="lockedCategory = null"
    />
  </Teleport>
</template>

<style scoped>
/* Logo, menu-items en overige links in de header erven anders de
   globale a { text-decoration: underline } uit main.css. */
header :deep(a) {
  text-decoration: none;
}

/* ⋯-knop: gedimd zichtbaar, vol bij hover/focus of zolang het menu
   open staat. Volledig verstoppen tot hover bleek in gebruikerstests
   onvindbaar (zelfde afweging als de rij-knoppen in de sidebar). */
.category-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  margin-left: -4px;
  margin-right: 2px;
  border-radius: 5px;
  color: theme('colors.gray.500');
  opacity: 0.45;
}
.category-item:hover .category-menu-btn,
.category-item:focus-within .category-menu-btn,
.category-menu-btn.menu-open {
  opacity: 1;
}
.category-menu-btn:hover {
  background: theme('colors.gray.200');
  color: theme('colors.gray.800');
}

.category-item[draggable='true'] {
  cursor: grab;
}

.drop-indicator {
  position: absolute;
  top: 12px;
  bottom: 12px;
  width: 2px;
  background: theme('colors.primary.600');
  border-radius: 1px;
  z-index: 1;
}

</style>
